import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import * as L from 'leaflet';
import 'src/assets/map-control/L.control.HtmlLegend.js';

import { MapConstants } from 'src/app/utils/map.constant';
import { UtilsService } from 'src/app/utils/utils.service';
import { isNullOrUndefined } from 'util';

import { ActualRainfallConstants } from '../constant/actual-rainfall.constant';
import { LandingPageService } from './../../../../landing-page/landing-page.service';
import { DataService } from './data.service';
import { ProjConstants } from 'src/app/utils/proj.constant';

@Injectable({
  providedIn: 'root'
})
export class MapService {

  stateBoundary: any;
  basinBoundary: any;

  constructor(
    public observedWeatherDataService: DataService,
    private utils: UtilsService,
    private http: HttpClient,
    private landingService: LandingPageService
  ) { }

  public createLayer(layer, gis) {
    if (layer.serviceType == 'WmsLayer') {
      return this.createWmsLayer(layer);
    }
    if (layer.serviceType == 'VectorLayer') {
      return this.createVectorLayer(layer, gis);
    }
    if (layer.serviceType == 'HeatMap') {
      return this.createHeatMapLayer(layer, gis);
    }
    if (layer.serviceType == 'CroppedHeatMap') {
      return this.createCroppedHeatMapLayer(layer, gis);
    }
    if (layer.serviceType == 'GeoJsonMarker') {
      return this.createGeoJsonLayer(layer, gis);
    } else {
      return new Promise((resolve) => { resolve(true); });
    }
  }

  createVectorLayer(layer, gis) {
    const options = {
      interactive: true,
      rendererFactory: {},
      opacity: 1,
      minZoom: 5,
      maxZoom: 18,
      pane: 'district',
      visible: true,
      transparent: true,
      vectorTileLayerStyles: {},
      getFeatureId: {}
    };
    options['rendererFactory'] = (L as any).canvas.tile;
    options['getFeatureId'] = (feature) => feature.properties.UUID;
    options['vectorTileLayerStyles'][layer.layerName] = () => {
      const style = {
        opacity: 0,
      };
      return style;
    };
    const url = MapConstants.GEOSERVER_WMTS_URL + '&layer=forecast:' + layer.layerName;
    layer['layer'] = (L as any).vectorGrid.protobuf(url, options);
    return layer;
  }

  createWmsLayer(layer) {
    const options = {
      layers: layer.layerName,
      opacity: 1,
      format: 'image/png',
      visible: true,
      transparent: true,
      pane: layer.zIndex ? layer.zIndex : 'district',
      fadeAnimation: false
    };
    if (layer.cql_filter != null && layer.cql_filter != undefined) {
      options['cql_filter'] = layer.cql_filter;
    }
    if (layer.pane != null && layer.pane != undefined) {
      options['pane'] = layer.pane;
    }
    if (layer.minZoom != null && layer.minZoom != undefined) {
      options['minZoom'] = layer.minZoom;
    }
    if (layer.stateName != null && layer.stateName != undefined) {
      options['viewParams'] = 'stateName:' + layer.stateName;
    }
    if (layer.env != null && layer.env != undefined) {
      options['env'] = layer.env;
    }
    if (layer.styles) {
      options['styles'] = layer.styles
    }
    const url = MapConstants.GEOSERVER_WMS_URL;
    layer['layer'] = (L as any).tileLayer.wms(url, options);
    return layer;
  }

  createHeatMapLayer(layer, gis) {
    const layerPromise = new Promise((resolve) => {
      const options = {
        opacity: 1,
        pane: 'heatMap',
        minZoom: 6,
        maxZoom: 15
      };

      let bounds = {
        y_min: 6.33,
        x_min: 67.57,
        y_max: 35.40,
        x_max: 97.17
      };

      const latlongs = [
        [bounds.y_min, bounds.x_min],
        [bounds.y_max, bounds.x_max]
      ];

      if (layer.url != null && layer.url != undefined) {
        this.observedWeatherDataService.getHeatMapData(layer.url, layer.postData).subscribe(data => {
          if (data != null) {
            // options['clip'] = data;
          }
          if (layer.url != null && layer.url != undefined) {
            let obj = this.observedWeatherDataService.setMapData(layer['heatMapImageBound']);
            const imgBounds = this.getImageBounds(obj['bounds']);
            const imageLayer = L.imageOverlay(data['ACTUAL'], imgBounds, options);
            layer['layer'] = imageLayer
            resolve(layer);
          }
        });
      }
    });

    return layerPromise;
  }

  getImageBounds(imageBounds) {
    const anchors = [
      [imageBounds[1][0], imageBounds[0][1]], // Top-Left: (y_max, x_min)
      [imageBounds[1][0], imageBounds[1][1]], // Top-Right: (y_max, x_max)
      [imageBounds[0][0], imageBounds[1][1]], // Bottom-Right: (y_min, x_max)
      [imageBounds[0][0], imageBounds[0][1]]  // Bottom-Left: (y_min, x_min)
    ];
    return anchors;
  }

  createCroppedHeatMapLayer(layer, gis) {
    const layerPromise = new Promise((resolve) => {
      const options = {
        opacity: 1,
        pane: 'heatMap',
        minZoom: 6,
        maxZoom: 15
      };
      let bounds = {
        y_min: 12.6,
        x_min: 74.765,
        y_max: 27.785,
        x_max: 84.78500000000001,
      };
      if (gis.urlParams.view == 'BASIN') {
        bounds = {
          y_min: 11.15,
          x_min: 73.3,
          y_max: 22.7,
          x_max: 85.25
        };
      }
      const anchors = [[bounds.y_max, bounds.x_min],
      [bounds.y_max, bounds.x_max], [bounds.y_min, bounds.x_max],
      [bounds.y_min, bounds.x_min]];
      this.getShapeBoundaryLayers(gis.urlParams.view, true, layer.stateName).then(
        data => {
          if (data != null) {
            options['clip'] = data;
          }
          if (layer.url != null && layer.url != undefined) {
            this.observedWeatherDataService.getHeatMapData(layer.url, layer.postData).subscribe(
              data => {
                if (data != null && data != undefined) {
                  layer['layer'] = L.imageTransform(data, anchors, options);
                }
                resolve(layer);
              });
          }
        });
    });
    return layerPromise;
  }

  getShapeBoundaryLayers(view, clip?, stateName?) {
    const shapeBoundary = new Promise((resolve, reject) => {
      if (clip == false) {
        resolve(null);
      }
      if (view == 'Admin') {
        if (this.stateBoundary == null || this.stateBoundary == undefined) {
          const layerName = 'odisha' + ':' + ProjConstants.STATE_TO_LAYERTYPE_NAME['INDIA_BOUNDARY'];
          const boundaryURL = MapConstants.GEOJSON_BOUNDARY_LAYER_URL + '&typeName=' + layerName
          this.http.get(boundaryURL).subscribe(
            data => {
              this.stateBoundary = data['features'][0]['geometry'];
              resolve(this.stateBoundary);
            });
        } else {
          resolve(this.stateBoundary);
        }
      }
    });
    return shapeBoundary;
  }

  createGeoJsonLayer(layer, gis) {
    const filter = layer.Filter;
    const promise = new Promise((resolve) => {
      this.observedWeatherDataService.getGeoJsonLayerData(layer.url, filter)
        .subscribe((geodata) => {
          this.observedWeatherDataService.getdataGis(layer.formattingUrl, layer.postData)
            .subscribe((data) => {
              const formattedData = this.observedWeatherDataService.formatdata(layer, geodata, data);
              layer['layer'] = this.addMarkerLayer(formattedData, layer, gis.map, gis.urlParams);
              resolve(layer);
            });
        });
    });
    return promise;
  }

  addMarkerLayer(geoJsonData: any, layer: string, map: L.Map, urlParams) {
    const vm = this;
    const geoJsonLayer = L.geoJSON(geoJsonData, {
      style: function (feature) { return this.stylingforGeoJson(feature, layer['LayerName']); },
      pointToLayer: function (feature, latlng) {
        if (layer['PointToLayer']) { return vm[layer['PointToLayer']](feature, latlng, map, urlParams); }
      },
      onEachFeature: function (feature, latlng) { if (layer['OnEachFeature']) { return this[layer['OnEachFeature']](feature, latlng, map); } },
      pane: 'marker'
    });
    return geoJsonLayer;
  }

  addTimeBasedLayers(gis, layerList) {
    let layerObjPromises = [];
    for (const i in layerList) {
      if (!isNullOrUndefined(i)) {
        const layer = layerList[i];
        if (layer['type'] != undefined && layer['type'] == 'Time') {
          const layerObj = this.createLayer(layer, gis);
          layerObj.then(
            data => {
              if (data['layer'] != undefined && data['layer'] != null) {
                if (!isNullOrUndefined(layer['layerName'])) {
                  if (!isNullOrUndefined(gis.htmlLegend)) {
                    gis.map.removeControl(gis.htmlLegend);
                  }
                  const htmlLegend = this.createLegend();
                  gis.map.addControl(htmlLegend);
                  gis.htmlLegend = htmlLegend;

                  const legendInfoData = this.getLegendData(ActualRainfallConstants.legendData, data['layer']);
                  htmlLegend.addLegend(legendInfoData);
                }
                data['layer'].addTo(gis.map);
                gis.activeLayers.push(data);
              }
              layerList[i] = data;
            }
          );
          layerObjPromises = layerObjPromises.concat(layerObj);
        }
      }
    }
    Promise.all(layerObjPromises).then(() => {
      // this.landingService.startCarousel.next(true);
      this.landingService.throbberRunningStatus.throbberAR = false;
      gis.showThrobber = false;
    });
  }

  removeAllTimeLayers(gis) {
    for (const index in gis.activeLayers) {
      if (!isNullOrUndefined(index)) {
        const layer = gis.activeLayers[index];
        if (layer['type'] == 'Time') {
          gis.map.removeLayer(layer['layer']);
          gis.activeLayers[index] = 'deleted';
        }
      }
    }
    gis.activeLayers = gis.activeLayers.filter(item => item != 'deleted');
  }

  removeAndAddInverseMask(gis, inverseInfo) {
    this.removeInverseMaskLayers(gis);
    this.addInverseMaskLayer(gis, inverseInfo);
  }

  removeInverseMaskLayers(gis) {
    for (const index in gis.activeLayers) {
      if (!isNullOrUndefined(index)) {
        const layer = gis.activeLayers[index];
        if (layer['type'] == 'Inverse') {
          gis.map.removeLayer(layer['layer']);
          gis.activeLayers[index] = 'deleted';
        }
      }
    }
    gis.activeLayers = gis.activeLayers.filter(item => item != 'deleted');
  }

  addInverseMaskLayer(gis, info) {
    if (isNullOrUndefined(info)) { return; }
    for (const index in gis.layerList) {
      if (!isNullOrUndefined(index)) {
        const layer = gis.layerList[index];
        if (layer['type'] == 'Inverse' && layer['name'] == info.name && layer['view'] == gis.view) {
          layer['cql_filter'] = info.cql_info;
          layer['hidelayer'] = false;
          const layerObj = this.createLayer(layer, gis);
          if (layerObj['layer'] != undefined && layerObj['layer'] != null) {
            layerObj['layer'].addTo(gis.map);
            gis.activeLayers = gis.activeLayers.concat(layerObj);
          }
          gis.layerList[index] = layerObj;
        }
      }
    }
  }

  createLegend() {
    return new (L as any).Control.HtmlLegend({
      position: 'bottomright',
      collapseSimple: false,
      detectStretched: false,
      collapsedOnInit: false
    });
  }

  getLegendData(data, layerInstance) {
    const legend = {
      name: data.title,
      layer: layerInstance,
    };
    legend['elements'] = [];
    const isTrue = 1;
    if (isTrue == 1 && data.levels.length > 0) {
      let html = ``;
      for (let key = 0; key < data.levels.length; key++) {
        if (data.levels[key].image) {
          html += `<p style='height:30px;margin:0px;padding:4px;color:black;background-color: ${data.levels[key].color}'>
           ${data.levels[key].name} <img src='${data.levels[key].image}'></p>`;
        } else {
          html += `<p style='height:20px;margin:0px;padding:4px;color:black;background-color: ${data.levels[key].color}'>${data.levels[key].name}</p>`;
        }
      }
      legend['elements'].push({ html });
    } else if (data.levels) {
      let obj;
      if (!(data.levels instanceof Array)) {
        const periodWiseLegend = {};
        for (const period in data.levels) {
          if (!isNullOrUndefined(period)) {
            const tempLegend = {
              name: data.title,
              layer: layerInstance,
              elements: []
            };
            data.levels[period].forEach(level => {
              obj = {};
              obj['html'] = '';
              if (level['image'] != undefined && level['image'] != '') {
                obj['html'] = '<img style=\'width:13px;height:13px;\' src=\'' + level['image'] + '\'/>';
              }
              obj['label'] = level.name;
              obj['style'] = { 'background-color': level.color, width: '13px', height: '13px' };
              tempLegend.elements.push(obj);
            });
            periodWiseLegend[period] = tempLegend;
          }
        }
        return periodWiseLegend;
      } else {
        data.levels.forEach(level => {
          obj = {};
          obj['html'] = '';
          if (level['image'] != undefined && level['image'] != '') {
            obj['html'] = '<img style=\'width:13px;height:13px;\' src=\'' + level['image'] + '\'/>';
          }
          obj['label'] = level.name;
          obj['style'] = { 'background-color': level.color, width: '13px', height: '13px' };
          legend['elements'].push(obj);
        });
      }
    }
    return legend;
  }
}
